import { jsPDF } from "jspdf";
import { formatDateTime, formatYmd } from "./dateutil";
import { DANCING_SCRIPT_B64 } from "./dancingScriptFont";
import { LIB_SANS_B64, LIB_SANS_BOLD_B64 } from "./liberationSansFont";
import en from "./i18n/locales/en.json";
import de from "./i18n/locales/de.json";
import pl from "./i18n/locales/pl.json";
import es from "./i18n/locales/es.json";
import it from "./i18n/locales/it.json";
import ro from "./i18n/locales/ro.json";
import fr from "./i18n/locales/fr.json";

// Shared PDF context — company + T&C acceptance trail — set once from auth so
// every export stamps the owner's accepted Terms version/timestamp in the footer.
let _ctx = { company: "", terms: null };
export function setPdfContext(c) { _ctx = { ..._ctx, ...(c || {}) }; }

function pad(n) { return String(n).padStart(2, "0"); }

// ---- PDF localization (reads the active language from localStorage; strict English fallback) ----
const PDF_DICTS = { en, de, pl, es, it, ro, fr };
// Languages whose diacritics are outside Latin-1/WinAnsi and therefore need the
// embedded Unicode font (Polish, Romanian). German/Spanish/Italian render fine
// in jsPDF's built-in Helvetica, so they keep it (zero layout change).
const UNICODE_LANGS = ["pl", "ro"];
function pdfLang() { try { const s = localStorage.getItem("safebook_lang"); return PDF_DICTS[s] ? s : "en"; } catch (e) { return "en"; } }
function pt(key) {
  const d = PDF_DICTS[pdfLang()];
  const v = d && d.pdf && d.pdf[key];
  if (v != null) return v;
  const b = en.pdf && en.pdf[key];
  return b != null ? b : key;
}
// Translate a KNOWN English table-header / metadata label passed in from call sites;
// unknown / dynamic labels pass through unchanged.
const TL_MAP = {
  "item": "hItem", "item / checkpoint": "hCheckpoint", "checkpoint": "hCheckpoint", "result": "hResult",
  "note": "hNote", "notes": "hNote", "status": "hStatus", "reading": "hReading", "corrective action": "hCorrective",
  "cleaning task": "hCleaningTask", "frequency": "hFrequency", "item / surface": "hItemSurface",
  "precautions / ppe": "hPrecautions", "method / steps": "hMethod", "attraction": "attraction", "site": "site",
  "operator": "mOperator", "performed by": "mPerformedBy", "check date": "checkDate", "filed on": "filedOn",
  "date & time": "mDateTime", "date and time": "mDateTime", "serial / tag no": "mSerial", "serial/tag no": "mSerial",
  "technician": "mTechnician", "work type": "mWorkType", "trainer": "mTrainer", "trainee": "mTrainee",
};
function tl(label) { const k = TL_MAP[String(label).trim().toLowerCase()]; return k ? pt(k) : String(label); }
// Translate a KNOWN English result/status token (pills); free-text notes & readings pass through unchanged.
const PV_MAP = {
  "PASS": "vPass", "PASSED": "vPass", "FAIL": "vFail", "FAILED": "vFail", "N/A": "vNa", "NA": "vNa",
  "REPAIRED": "vRepaired", "ACCEPTED": "vAccepted", "REJECTED": "vRejected", "COMPLETED": "vCompleted",
  "NOT DONE": "vNotDone", "YES": "vYes", "NO": "vNo", "HIGH WIND": "vHighWind", "OUT OF SERVICE": "vOutOfService",
};
function pv(value) { const k = PV_MAP[String(value).trim().toUpperCase()]; return k ? pt(k) : String(value); }

const SESSION_KEYS = { opening: "sessOpening", service: "sessService", midday: "sessMidday", closing: "sessClosing", weekly: "sessWeekly" };
const CLEAN_FREQ_KEYS = { after_use: "freqAfterUse", every_shift: "freqEveryShift", daily: "freqDaily", weekly: "freqWeekly", other: "freqOther" };
function sessionLabel(k) { return SESSION_KEYS[k] ? pt(SESSION_KEYS[k]) : ""; }
function cleanFreqLabel(k) { return CLEAN_FREQ_KEYS[k] ? pt(CLEAN_FREQ_KEYS[k]) : pt("freqOther"); }

// Court-proof document reference: SB-<TYPE>-DDMMYYYY-SHORTID
// TYPE codes: DC daily-check · WC wind · MR maintenance/repair · IR incident · TR training · RP report
export function docRefId(docType, recordId) {
  const d = new Date();
  const stamp = `${pad(d.getDate())}${pad(d.getMonth() + 1)}${d.getFullYear()}`;
  const raw = recordId ? String(recordId).replace(/[^a-zA-Z0-9]/g, "") : Math.random().toString(36).slice(2);
  const short = raw.slice(0, 8).toUpperCase().padEnd(8, "0");
  return `SB-${docType || "DC"}-${stamp}-${short}`;
}

// Derive operator initials (explicit wins; else first letters of the name).
export function initialsOf(name = "", explicit = "") {
  if (explicit && String(explicit).trim()) return String(explicit).trim().toUpperCase().slice(0, 6);
  return String(name || "").split(/\s+/).filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 4);
}

export function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const FOOTER = 46; // reserved bottom band for doc-ref, T&C stamp and legal note

// Embed the "Dancing Script" cursive font so typed signatures render identically in the PDF.
function registerSigFont(doc) {
  try {
    doc.addFileToVFS("DancingScript.ttf", DANCING_SCRIPT_B64);
    doc.addFont("DancingScript.ttf", "DancingScript", "normal");
    return true;
  } catch (e) { return false; }
}

// Register the base document font. For Polish/Romanian we embed a Unicode TTF
// (Liberation Sans, Arial-metric) so diacritics render; everything else keeps
// jsPDF's built-in Helvetica. The chosen family is stored on doc.__ff and every
// text call routes through setF() below (layout/positions are untouched).
function initFont(doc) {
  if (UNICODE_LANGS.includes(pdfLang())) {
    try {
      doc.addFileToVFS("LiberationSans.ttf", LIB_SANS_B64);
      doc.addFont("LiberationSans.ttf", "LiberationSans", "normal");
      doc.addFileToVFS("LiberationSans-Bold.ttf", LIB_SANS_BOLD_B64);
      doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");
      doc.__ff = "LiberationSans";
      return;
    } catch (e) { /* fall back to helvetica */ }
  }
  doc.__ff = "helvetica";
}
function setF(doc, style) {
  const ff = doc.__ff || "helvetica";
  if (ff === "helvetica") { doc.setFont("helvetica", style); return; }
  doc.setFont(ff, style === "bold" ? "bold" : "normal"); // embedded font has normal + bold only
}

// Builds the PDF document (no save).
// meta: { details, signature, signedBy, initials, submittedAt, docType, recordId,
//         siteLocation, attractionName, terms, trainee, signLabel }
function buildDoc(title, company, rows, columns, photos, meta) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const sigFont = registerSigFont(doc);
  initFont(doc);
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 40;
  let y = M;
  const m = meta || {};
  const co = company || _ctx.company || "";
  const ref = m.docRef || docRefId(m.docType, m.recordId);
  const ensure = (need) => { if (y + need > pageH - M - FOOTER) { doc.addPage(); y = M; } };

  // ---- Header ----
  doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(20);
  doc.text("SafeBook", M, y);
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(8);
  doc.text(`${pt("docRef")} ${ref}`, pageW - M, y - 8, { align: "right" });
  y += 22;
  doc.setTextColor(51, 65, 85); setF(doc, "bold"); doc.setFontSize(14);
  doc.text(title, M, y); y += 18;
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(10);
  doc.text(`${co ? co + " — " : ""}${formatDateTime(new Date())}`, M, y); y += 14;
  const band = [];
  if (m.attractionName) band.push(`${pt("attraction")}: ${m.attractionName}`);
  if (m.siteLocation) band.push(`${pt("site")}: ${m.siteLocation}`);
  if (band.length) { doc.setFontSize(9); doc.text(band.join("     |     "), M, y); y += 14; }
  // Backdated ("Missed a day?") audit line: logical Check Date + immutable Filed On.
  if (m.checkDate) {
    doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(9);
    doc.text(`${pt("checkDate")}: ${formatYmd(m.checkDate)}     |     ${pt("filedOn")}: ${m.filedAt || formatDateTime(new Date())}`, M, y); y += 14;
    if (m.backdated) {
      const label = pt("filedRetro");
      setF(doc, "bold"); doc.setFontSize(8);
      const w = doc.getTextWidth(label) + 14;
      doc.setFillColor(220, 38, 38); doc.roundedRect(M, y - 2, w, 15, 3, 3, "F");
      doc.setTextColor(255, 255, 255); doc.text(label, M + 7, y + 8);
      y += 20;
      doc.setTextColor(100, 116, 139); setF(doc, "normal");
    }
  }
  doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 12;

  // ---- Detail rows ----
  if (m.details && m.details.length) {
    m.details.filter((d) => d && d.value != null && String(d.value).trim() !== "").forEach((d) => {
      const val = doc.splitTextToSize(pv(String(d.value ?? "-")), pageW - M * 2 - 130);
      const h = Math.max(16, val.length * 12 + 4);
      ensure(h);
      doc.setTextColor(100, 116, 139); setF(doc, "bold"); doc.setFontSize(9);
      doc.text(tl(String(d.label)).toUpperCase(), M, y + 10);
      doc.setTextColor(15, 23, 42); setF(doc, "normal"); doc.setFontSize(11);
      doc.text(val, M + 130, y + 10);
      y += h; doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y);
    });
    y += 10;
  }

  // ---- Itemized table ----
  if (columns && columns.length && rows && rows.length) {
    const colW = (pageW - M * 2) / columns.length;
    const drawHeader = () => {
      doc.setFillColor(15, 23, 42); doc.rect(M, y, pageW - M * 2, 20, "F");
      doc.setTextColor(255, 255, 255); setF(doc, "bold"); doc.setFontSize(9);
      columns.forEach((c, i) => doc.text(tl(String(c.label)), M + i * colW + 4, y + 14));
      y += 20;
    };
    drawHeader();
    setF(doc, "normal"); doc.setTextColor(15, 23, 42);
    rows.forEach((r) => {
      let maxLines = 1;
      const cells = columns.map((c, i) => {
        doc.setFontSize(9);
        const main = doc.splitTextToSize(pv(String(c.get(r) ?? "-")), colW - 8);
        const subText = c.sub ? String(c.sub(r) ?? "").trim() : (i === 0 && r && r.item_id && r.description ? String(r.description).trim() : "");
        doc.setFontSize(8);
        const sub = subText ? doc.splitTextToSize(subText, colW - 8) : [];
        maxLines = Math.max(maxLines, main.length + sub.length);
        return { main, sub };
      });
      const rowH = maxLines * 11 + 6;
      if (y > pageH - M - FOOTER - rowH) { doc.addPage(); y = M; drawHeader(); setF(doc, "normal"); doc.setTextColor(15, 23, 42); }
      doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y);
      cells.forEach((cell, i) => {
        const x = M + i * colW + 4;
        setF(doc, "normal"); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
        doc.text(cell.main, x, y + 12);
        if (cell.sub.length) {
          setF(doc, "italic"); doc.setFontSize(8); doc.setTextColor(100, 116, 139);
          doc.text(cell.sub, x, y + 12 + cell.main.length * 11);
        }
      });
      setF(doc, "normal"); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
      y += rowH;
    });
    y += 14;
  }

  // ---- General Notes / Observations (rendered BELOW the itemized table — near the bottom, not the top) ----
  if (m.notes && String(m.notes).trim()) {
    ensure(70);
    doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(12);
    doc.text(m.notesLabel || pt("notes"), M, y); y += 8;
    doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 14;
    doc.setTextColor(15, 23, 42); setF(doc, "normal"); doc.setFontSize(10);
    const nlines = doc.splitTextToSize(String(m.notes).trim(), pageW - M * 2);
    nlines.forEach((ln) => { ensure(14); doc.text(ln, M, y + 8); y += 13; });
    y += 14;
  }

  // ---- Sign-Off & Audit Block(s) — uniform, dual for training ----
  const drawSignBlock = (sig, signedBy, ini, stamp, label) => {
    ensure(180);
    doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(12);
    doc.text(label, M, y); y += 8;
    doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 14;
    if (sig) {
      doc.setFillColor(255, 255, 255); doc.setDrawColor(203, 213, 225);
      doc.rect(M, y, 250, 86, "FD");
      if (String(sig).startsWith("data:")) {
        try { doc.addImage(sig, sig.includes("image/png") ? "PNG" : "JPEG", M + 6, y + 6, 238, 74, undefined, "FAST"); } catch (e) {}
      } else {
        doc.setTextColor(15, 23, 42);
        doc.setFont(sigFont ? "DancingScript" : "times", sigFont ? "normal" : "italic");
        let fs = 34; doc.setFontSize(fs);
        while (doc.getTextWidth(String(sig)) > 236 && fs > 12) { fs -= 1; doc.setFontSize(fs); }
        doc.text(String(sig), M + 12, y + 54);
        setF(doc, "normal");
      }
      y += 96;
    }
    doc.setTextColor(15, 23, 42); setF(doc, "bold"); doc.setFontSize(11);
    doc.text(`${pt("signedBy")} ${signedBy || "-"}`, M, y); y += 16;
    doc.text(`${pt("initials")} ${initialsOf(signedBy, ini) || "-"}`, M, y); y += 16;
    setF(doc, "normal");
    doc.text(`${pt("submittedOn")} ${stamp || formatDateTime(new Date())}`, M, y); y += 18;
  };
  if (m.signedBy || m.signature) drawSignBlock(m.signature, m.signedBy, m.initials, m.submittedAt, m.signLabel || pt("signOffAudit"));
  if (m.trainee && (m.trainee.signature || m.trainee.name)) { y += 6; drawSignBlock(m.trainee.signature, m.trainee.name, m.trainee.initials, m.submittedAt, pt("traineeSignOff")); }

  // ---- Photo evidence ----
  const pics = (photos || []).filter((p) => p && p.dataUri);
  if (pics.length) {
    doc.addPage(); y = M;
    doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(14);
    doc.text(pt("photoEvidence"), M, y); y += 20;
    pics.forEach((p) => {
      const imgW = pageW - M * 2, imgH = 240;
      if (y + imgH + 24 > pageH - M - FOOTER) { doc.addPage(); y = M; }
      doc.setTextColor(51, 65, 85); setF(doc, "bold"); doc.setFontSize(10);
      doc.text(String(p.label || pt("photo")), M, y); y += 12;
      try { doc.addImage(p.dataUri, p.dataUri.includes("image/png") ? "PNG" : "JPEG", M, y, imgW, imgH, undefined, "FAST"); } catch (e) {}
      y += imgH + 20;
    });
  }

  // ---- Footer on every page: Doc Ref · page · T&C acceptance trail · legal note ----
  const note = pt("footerNote");
  const terms = m.terms || _ctx.terms;
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    setF(doc, "normal"); doc.setFontSize(7); doc.setTextColor(120, 130, 145);
    doc.text(`${pt("docRef")} ${ref}`, M, pageH - 38);
    doc.text(`${pt("page")} ${p} ${pt("of")} ${pageCount}`, pageW - M, pageH - 38, { align: "right" });
    if (terms && terms.version) {
      const tstamp = terms.acceptedAt ? formatDateTime(terms.acceptedAt) : "—";
      doc.text(`${pt("termsAccepted")} v${terms.version}${terms.acceptedBy ? " " + pt("by") + " " + terms.acceptedBy : ""} ${pt("on")} ${tstamp}`, M, pageH - 29);
    }
    setF(doc, "italic");
    const noteLines = doc.splitTextToSize(note, pageW - M * 2);
    doc.text(noteLines, pageW / 2, pageH - 18, { align: "center" });
  }
  const fname = `${title.replace(/[^\w]+/g, "_")}.pdf`.toLowerCase();
  return { doc, fname, ref };
}

// Direct-download PDF (no window.print).
export function exportPdf(title, company, rows, columns, photos, meta) {
  const { doc, fname } = buildDoc(title, company, rows, columns, photos, meta);
  doc.save(fname);
}

// Returns { file, fname, ref } for viewing/saving/sharing/emailing as an actual attachment.
export function pdfFile(title, company, rows, columns, photos, meta) {
  const { doc, fname, ref } = buildDoc(title, company, rows, columns, photos, meta);
  const blob = doc.output("blob");
  return { file: new File([blob], `${ref}.pdf`, { type: "application/pdf" }), fname, ref };
}

// Back-compat alias — direct-download PDF (never window.print).
export function printReport(title, company, rows, columns, photos, meta) {
  return exportPdf(title, company, rows, columns, photos, meta);
}

// STRICT file-only native share: attaches ONLY the physical .pdf File (no text/url,
// which prevents messaging apps injecting raw blob: strings). Falls back to an
// immediate local .pdf download when file sharing is unsupported.
export async function sharePdf(title, company, rows, columns, photos, meta) {
  const m = { ...(meta || {}) };
  const ref = m.docRef || docRefId(m.docType, m.recordId);
  m.docRef = ref;
  const { doc } = buildDoc(title, company, rows, columns, photos, m);
  const blob = doc.output("blob");
  const file = new File([blob], `${ref}.pdf`, { type: "application/pdf" });
  if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
    try { await navigator.share({ files: [file], title: ref }); return { shared: true, ref }; }
    catch (e) { if (e && e.name === "AbortError") return { shared: false, aborted: true, ref }; }
  }
  doc.save(`${ref}.pdf`);
  return { shared: false, ref };
}

// ============================================================================
// Multi-day DETAILED audit pack — one full itemized check sheet per logged day
// (chronological oldest→newest, empty days skipped). Item tables, notes and
// signature blocks are kept whole and never sliced across a page break.
// ============================================================================

function _tableSection(doc, y, rows, columns, pageW, pageH, M) {
  const colW = (pageW - M * 2) / columns.length;
  const drawHeader = () => {
    doc.setFillColor(15, 23, 42); doc.rect(M, y, pageW - M * 2, 20, "F");
    doc.setTextColor(255, 255, 255); setF(doc, "bold"); doc.setFontSize(9);
    columns.forEach((c, i) => doc.text(tl(String(c.label)), M + i * colW + 4, y + 14));
    y += 20;
  };
  drawHeader();
  setF(doc, "normal"); doc.setTextColor(15, 23, 42);
  rows.forEach((r) => {
    const isFail = !!(r && (r.status === "fail" || r.value === "fail"));
    let maxLines = 1;
    const cells = columns.map((c, i) => {
      doc.setFontSize(9);
      const main = doc.splitTextToSize(pv(String(c.get(r) ?? "-")), colW - 8);
      const subText = c.sub ? String(c.sub(r) ?? "").trim() : (i === 0 && r && r.item_id && r.description ? String(r.description).trim() : "");
      doc.setFontSize(8);
      const sub = subText ? doc.splitTextToSize(subText, colW - 8) : [];
      maxLines = Math.max(maxLines, main.length + sub.length);
      return { main, sub };
    });
    const rowH = maxLines * 11 + 6;
    if (y > pageH - M - FOOTER - rowH) { doc.addPage(); y = M; drawHeader(); setF(doc, "normal"); doc.setTextColor(15, 23, 42); }
    if (isFail) { doc.setFillColor(254, 226, 226); doc.rect(M, y, pageW - M * 2, rowH, "F"); }
    doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y);
    cells.forEach((cell, i) => {
      const x = M + i * colW + 4;
      setF(doc, isFail ? "bold" : "normal"); doc.setFontSize(9);
      if (isFail) doc.setTextColor(185, 28, 28); else doc.setTextColor(15, 23, 42);
      doc.text(cell.main, x, y + 12);
      if (cell.sub.length) { setF(doc, "italic"); doc.setFontSize(8); doc.setTextColor(isFail ? 185 : 100, isFail ? 28 : 116, isFail ? 28 : 139); doc.text(cell.sub, x, y + 12 + cell.main.length * 11); }
    });
    setF(doc, "normal"); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
    y += rowH;
  });
  return y + 14;
}

function _notesSection(doc, y, notes, pageW, pageH, M) {
  if (!notes || !String(notes).trim()) return y;
  if (y + 60 > pageH - M - FOOTER) { doc.addPage(); y = M; }
  doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(12);
  doc.text(pt("notes"), M, y); y += 8;
  doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 14;
  doc.setTextColor(15, 23, 42); setF(doc, "normal"); doc.setFontSize(10);
  const nlines = doc.splitTextToSize(String(notes).trim(), pageW - M * 2);
  nlines.forEach((ln) => { if (y + 14 > pageH - M - FOOTER) { doc.addPage(); y = M; } doc.text(ln, M, y + 8); y += 13; });
  return y + 14;
}

function _signSection(doc, y, sigFont, sig, signedBy, ini, stamp, label, pageW, pageH, M) {
  const needed = sig ? 200 : 100; // keep whole block on one page
  if (y + needed > pageH - M - FOOTER) { doc.addPage(); y = M; }
  doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(12);
  doc.text(label, M, y); y += 8;
  doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 14;
  if (sig) {
    doc.setFillColor(255, 255, 255); doc.setDrawColor(203, 213, 225); doc.rect(M, y, 250, 86, "FD");
    if (String(sig).startsWith("data:")) { try { doc.addImage(sig, sig.includes("image/png") ? "PNG" : "JPEG", M + 6, y + 6, 238, 74, undefined, "FAST"); } catch (e) {} }
    else { doc.setTextColor(15, 23, 42); doc.setFont(sigFont ? "DancingScript" : "times", sigFont ? "normal" : "italic"); let fs = 34; doc.setFontSize(fs); while (doc.getTextWidth(String(sig)) > 236 && fs > 12) { fs -= 1; doc.setFontSize(fs); } doc.text(String(sig), M + 12, y + 54); setF(doc, "normal"); }
    y += 96;
  }
  doc.setTextColor(15, 23, 42); setF(doc, "bold"); doc.setFontSize(11);
  doc.text(`${pt("signedBy")} ${signedBy || "-"}`, M, y); y += 16;
  doc.text(`${pt("initials")} ${initialsOf(signedBy, ini) || "-"}`, M, y); y += 16;
  setF(doc, "normal");
  doc.text(`${pt("submittedOn")} ${stamp || formatDateTime(new Date())}`, M, y); y += 18;
  return y;
}

function _footerAll(doc, ref, terms, pageW, pageH, M) {
  const note = pt("footerNote");
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    setF(doc, "normal"); doc.setFontSize(7); doc.setTextColor(120, 130, 145);
    doc.text(`${pt("docRef")} ${ref}`, M, pageH - 38);
    doc.text(`${pt("page")} ${p} ${pt("of")} ${pageCount}`, pageW - M, pageH - 38, { align: "right" });
    if (terms && terms.version) {
      const tstamp = terms.acceptedAt ? formatDateTime(terms.acceptedAt) : "—";
      doc.text(`${pt("termsAccepted")} v${terms.version}${terms.acceptedBy ? " " + pt("by") + " " + terms.acceptedBy : ""} ${pt("on")} ${tstamp}`, M, pageH - 29);
    }
    setF(doc, "italic");
    const noteLines = doc.splitTextToSize(note, pageW - M * 2);
    doc.text(noteLines, pageW / 2, pageH - 18, { align: "center" });
  }
}

function buildDetailedDoc(title, company, logs, kind, meta) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const sigFont = registerSigFont(doc);
  initFont(doc);
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 40;
  const m = meta || {};
  const co = company || _ctx.company || "";
  const ref = docRefId("RP");
  let y = M;

  const sorted = [...(logs || [])].sort((a, b) => {
    const da = a.check_date || String(a.submitted_at || "").slice(0, 10);
    const db = b.check_date || String(b.submitted_at || "").slice(0, 10);
    return String(da).localeCompare(String(db)) || String(a.submitted_at || "").localeCompare(String(b.submitted_at || ""));
  });

  doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(20);
  doc.text("SafeBook", M, y);
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(8);
  doc.text(`${pt("docRef")} ${ref}`, pageW - M, y - 8, { align: "right" });
  y += 22;
  doc.setTextColor(51, 65, 85); setF(doc, "bold"); doc.setFontSize(15);
  doc.text(title, M, y); y += 18;
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(10);
  doc.text(`${co ? co + " — " : ""}${formatDateTime(new Date())}`, M, y); y += 14;
  doc.setFontSize(9);
  const rangeTxt = m.rangeLabel ? `${pt("dateRange")} ${m.rangeLabel}     |     ` : "";
  doc.text(`${rangeTxt}${sorted.length} ${sorted.length !== 1 ? pt("checkSheets") : pt("checkSheet")}`, M, y); y += 14;
  doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 16;

  if (!sorted.length) {
    doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(11);
    doc.text(pt("noRecords"), M, y + 10);
  }

  sorted.forEach((log, idx) => {
    if (idx > 0) { doc.addPage(); y = M; } else { y += 4; }
    const isCleaning = log.log_type === "cleaning";
    doc.setTextColor(15, 23, 42); setF(doc, "bold"); doc.setFontSize(13);
    doc.text(String(log.attraction_name || "Attraction"), M, y);
    const badge = kind === "wind" ? (log.high_wind ? pt("vHighWind") : `${log.wind_speed} mph`) : pv(String(log.overall_result || "-").toUpperCase());
    const isFail = kind === "wind" ? !!log.high_wind : log.overall_result === "fail";
    setF(doc, "bold"); doc.setFontSize(9);
    const bw = doc.getTextWidth(badge) + 16;
    if (isFail) doc.setFillColor(220, 38, 38); else doc.setFillColor(16, 185, 129);
    doc.roundedRect(pageW - M - bw, y - 11, bw, 16, 3, 3, "F");
    doc.setTextColor(255, 255, 255); doc.text(badge, pageW - M - bw + 8, y);
    y += 16;
    doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(9);
    const cd = log.check_date ? formatYmd(log.check_date) : formatYmd(String(log.submitted_at || "").slice(0, 10));
    doc.text(`${pt("checkDate")}: ${cd}     |     ${pt("filedOn")}: ${formatDateTime(log.submitted_at)}`, M, y); y += 14;
    if (isCleaning) { doc.setTextColor(15, 23, 42); setF(doc, "bold"); doc.setFontSize(10); doc.text(pt("cleaningChecklistTitle"), M, y); y += 16; setF(doc, "normal"); doc.setTextColor(100, 116, 139); }
    else if (kind === "catering" && log.catering_type) { const cl = log.catering_type === "delivery" ? pt("goodsInDelivery") : `${pt("foodSafetyDash")} ${sessionLabel(log.catering_type)} ${pt("checksSuffix")}`; doc.setTextColor(15, 23, 42); setF(doc, "bold"); doc.setFontSize(10); doc.text(cl, M, y); y += 16; setF(doc, "normal"); doc.setTextColor(100, 116, 139); }
    if (log.backdated) {
      const bl = pt("filedRetro");
      setF(doc, "bold"); doc.setFontSize(8);
      const w = doc.getTextWidth(bl) + 14;
      doc.setFillColor(220, 38, 38); doc.roundedRect(M, y - 2, w, 15, 3, 3, "F");
      doc.setTextColor(255, 255, 255); doc.text(bl, M + 7, y + 8); y += 20;
      doc.setTextColor(100, 116, 139); setF(doc, "normal");
    }
    if (kind === "wind") {
      doc.setTextColor(15, 23, 42); setF(doc, "normal"); doc.setFontSize(10);
      doc.text(`${pt("windSpeed")} ${log.wind_speed ?? "-"} mph${log.high_wind ? "  —  " + pt("exceedsLimit") : ""}`, M, y + 4); y += 20;
    }
    doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 12;

    const results = log.results || [];
    if ((kind === "checks" || kind === "catering" || isCleaning) && results.length) {
      const columns = isCleaning ? [
        { label: "Cleaning Task", get: (r) => r.label },
        { label: "Frequency", get: (r) => (r.frequency ? cleanFreqLabel(r.frequency) : "-") },
        { label: "Status", get: (r) => (r.status === "fail" || r.value === "fail" ? "NOT DONE" : "COMPLETED") },
        { label: "Note", get: (r) => r.note || "-" },
      ] : kind === "catering" ? [
        { label: "Item", get: (r) => r.label },
        { label: "Reading", get: (r) => (r.value != null && r.value !== "" ? `${r.value}${r.unit || ""}` : "-") },
        { label: "Result", get: (r) => (r.status ? String(r.status).toUpperCase() : (r.value ? String(r.value).toUpperCase() : "-")) },
        { label: "Corrective Action", get: (r) => r.corrective || "-" },
      ] : [
        { label: "Item", get: (r) => r.label },
        { label: "Result", get: (r) => (r.value != null && r.value !== "" ? String(r.value).toUpperCase() : "-") },
        { label: "Note", get: (r) => r.note || "-" },
      ];
      y = _tableSection(doc, y, results, columns, pageW, pageH, M);
    }
    y = _notesSection(doc, y, log.general_notes, pageW, pageH, M);
    y = _signSection(doc, y, sigFont, log.signature, log.signed_by_name, log.signed_by_initials, formatDateTime(log.submitted_at), kind === "wind" ? pt("windSignOff") : pt("signOffAudit"), pageW, pageH, M);
  });

  const matrices = (m.allergenMatrices || []).filter((x) => x && x.matrix && ((x.matrix.dishes || []).length));
  if (kind === "catering" && matrices.length) {
    matrices.forEach((mx) => { doc.addPage(); let ay = M; _drawAllergenGrid(doc, ay, mx.unitName, mx.matrix, pageW, pageH, M); });
  }
  const schedules = (m.cleaningSchedules || []).filter((x) => x && ((x.items || []).length));
  if (schedules.length) {
    schedules.forEach((s) => { doc.addPage(); let cy = M; _drawCleaningSchedule(doc, cy, s.unitName, s.items, pageW, pageH, M); });
  }

  _footerAll(doc, ref, m.terms || _ctx.terms, pageW, pageH, M);
  const fname = `${title.replace(/[^\w]+/g, "_")}.pdf`.toLowerCase();
  return { doc, fname, ref };
}

export function exportDetailedLogs(title, company, logs, kind, meta) {
  const { doc, fname } = buildDetailedDoc(title, company, logs, kind, meta);
  doc.save(fname);
}

export function detailedLogsFile(title, company, logs, kind, meta) {
  const { doc, ref } = buildDetailedDoc(title, company, logs, kind, meta);
  const blob = doc.output("blob");
  return { file: new File([blob], `${ref}.pdf`, { type: "application/pdf" }), ref };
}

// ============================================================================
// Customer Delivery Handover — dual-signature certificate (inflatables)
// ============================================================================
function renderDeliveryCert(doc, d, sigFont) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 40;
  let y = M;
  const co = d.company || _ctx.company || "";
  const ref = docRefId("DH", d.id);
  const submittedAt = d.submitted_at || new Date().toISOString();
  const ensure = (need) => { if (y + need > pageH - M - FOOTER) { doc.addPage(); y = M; } };

  doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(20);
  doc.text("SafeBook", M, y);
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(8);
  doc.text(`${pt("docRef")} ${ref}`, pageW - M, y - 8, { align: "right" });
  y += 22;
  doc.setTextColor(51, 65, 85); setF(doc, "bold"); doc.setFontSize(15);
  doc.text(pt("deliveryCertTitle"), M, y); y += 18;
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(10);
  doc.text(`${co ? co + " — " : ""}${formatDateTime(submittedAt)}`, M, y); y += 16;
  doc.setTextColor(15, 23, 42); setF(doc, "bold"); doc.setFontSize(12);
  doc.text(String(d.attraction_name || "Inflatable"), M, y); y += 8;
  doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 16;

  const section = (t) => { ensure(30); doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(12); doc.text(t, M, y); y += 8; doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 14; doc.setTextColor(15, 23, 42); setF(doc, "normal"); doc.setFontSize(10); };
  const line = (lbl, val) => { const s = doc.splitTextToSize(String(val || "-"), pageW - M * 2 - 130); ensure(Math.max(16, s.length * 13)); setF(doc, "bold"); doc.setTextColor(71, 85, 105); doc.text(lbl, M, y); setF(doc, "normal"); doc.setTextColor(15, 23, 42); doc.text(s, M + 130, y); y += Math.max(16, s.length * 13); };

  section(pt("secHandoverDetails"));
  line(pt("lblDeliveredBy"), d.delivered_by || d.driver_name);
  line(pt("lblCustomerName"), d.customer_name);
  line(pt("lblCustomerEmail"), d.customer_email);
  line(pt("lblHandoverDate"), formatDateTime(submittedAt));
  y += 6;

  section(pt("secSetupAnchorage"));
  line(pt("lblSurfaceType"), d.surface_type);
  y += 6;

  if (d.pre_existing_notes && String(d.pre_existing_notes).trim()) {
    section(pt("secPreExisting"));
    doc.splitTextToSize(String(d.pre_existing_notes).trim(), pageW - M * 2).forEach((ln) => { ensure(14); doc.text(ln, M, y + 2); y += 13; });
    y += 8;
  }

  section(pt("secAgreements"));
  const agreements = [pt("agreeWind"), pt("agreeSupervision"), pt("agreeNoRelocation")];
  agreements.forEach((a) => { const s = doc.splitTextToSize(a, pageW - M * 2 - 24); ensure(Math.max(16, s.length * 12) + 4); doc.setTextColor(16, 185, 129); setF(doc, "bold"); doc.text("[X]", M, y + 2); doc.setTextColor(15, 23, 42); setF(doc, "normal"); doc.text(s, M + 24, y + 2); y += Math.max(16, s.length * 12) + 4; });
  y += 4;

  section(pt("secLegal"));
  const legal = pt("legalDeclaration");
  setF(doc, "italic"); doc.setFontSize(9); doc.setTextColor(51, 65, 85);
  doc.splitTextToSize(legal, pageW - M * 2).forEach((ln) => { ensure(13); doc.text(ln, M, y + 2); y += 12; });
  setF(doc, "normal"); y += 12;

  // Dual signatures side by side (driver = typed cursive sign-off; customer = drawn image)
  ensure(150);
  const colW = (pageW - M * 2 - 20) / 2;
  const boxY = y;
  const drawSig = (x, sig, who, name) => {
    doc.setFillColor(255, 255, 255); doc.setDrawColor(203, 213, 225); doc.rect(x, boxY, colW, 80, "FD");
    if (sig) {
      if (String(sig).startsWith("data:")) {
        try { doc.addImage(sig, sig.includes("image/png") ? "PNG" : "JPEG", x + 4, boxY + 4, colW - 8, 72, undefined, "FAST"); } catch (e) {}
      } else {
        doc.setTextColor(15, 23, 42); doc.setFont(sigFont ? "DancingScript" : "times", sigFont ? "normal" : "italic");
        let fs = 30; doc.setFontSize(fs);
        while (doc.getTextWidth(String(sig)) > colW - 16 && fs > 10) { fs -= 1; doc.setFontSize(fs); }
        doc.text(String(sig), x + 8, boxY + 48);
        setF(doc, "normal");
      }
    }
    doc.setTextColor(71, 85, 105); setF(doc, "bold"); doc.setFontSize(9);
    doc.text(who, x, boxY + 94);
    setF(doc, "normal"); doc.setTextColor(15, 23, 42); doc.setFontSize(10);
    doc.text(String(name || "-"), x, boxY + 108);
    doc.setTextColor(100, 116, 139); doc.setFontSize(8);
    doc.text(formatDateTime(submittedAt), x, boxY + 120);
  };
  drawSig(M, d.driver_signature, pt("sigDriver"), d.delivered_by || d.driver_name);
  drawSig(M + colW + 20, d.customer_signature, pt("sigCustomer"), d.customer_name);
  y = boxY + 132;

  if (d.setup_photo) {
    doc.addPage(); y = M;
    doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(14);
    doc.text(pt("proofOfSetup"), M, y); y += 20;
    try { doc.addImage(d.setup_photo, d.setup_photo.includes("image/png") ? "PNG" : "JPEG", M, y, pageW - M * 2, 360, undefined, "FAST"); } catch (e) {}
  }
  return ref;
}

function buildDeliveryDoc(d) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const sigFont = registerSigFont(doc);
  initFont(doc);
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 40;
  const ref = renderDeliveryCert(doc, d, sigFont);
  _footerAll(doc, ref, _ctx.terms, pageW, pageH, M);
  return { doc, ref };
}

export function exportDelivery(d) {
  const { doc, ref } = buildDeliveryDoc(d);
  doc.save(`${ref}.pdf`);
}

export function deliveryCertFile(d) {
  const { doc, ref } = buildDeliveryDoc(d);
  const blob = doc.output("blob");
  const base64 = doc.output("datauristring");
  return { file: new File([blob], `${ref}.pdf`, { type: "application/pdf" }), ref, base64 };
}

// Batch export for the Audit Log / Reports Center — one full certificate per
// handover (details, agreements, dual signatures + the setup photo).
function buildDeliveriesDoc(company, data) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const sigFont = registerSigFont(doc);
  initFont(doc);
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 40;
  (data || []).forEach((d, i) => {
    if (i > 0) doc.addPage();
    renderDeliveryCert(doc, { ...d, company: d.company || company }, sigFont);
  });
  const ref = docRefId("DH", "batch");
  _footerAll(doc, ref, _ctx.terms, pageW, pageH, M);
  return { doc, ref };
}

export function exportDeliveries(company, data, opts = {}) {
  const { doc } = buildDeliveriesDoc(company, data);
  doc.save("delivery-handovers.pdf");
}

export function deliveriesFile(company, data) {
  const { doc, ref } = buildDeliveriesDoc(company, data);
  const blob = doc.output("blob");
  return { file: new File([blob], "delivery-handovers.pdf", { type: "application/pdf" }), ref };
}


// ============================================================================
// FSA 14-Allergen Matrix — printable grid (per catering unit). Rendered both as
// a standalone "window display" PDF and appended to the catering EHO Audit Pack.
// ============================================================================
const FSA_ALLERGENS_PDF = [
  { key: "celery", i18nKey: "alCelery", code: "CE" },
  { key: "gluten", i18nKey: "alGluten", code: "GL" },
  { key: "crustaceans", i18nKey: "alCrustaceans", code: "CR" },
  { key: "eggs", i18nKey: "alEggs", code: "EG" },
  { key: "fish", i18nKey: "alFish", code: "FI" },
  { key: "lupin", i18nKey: "alLupin", code: "LU" },
  { key: "milk", i18nKey: "alMilk", code: "MK" },
  { key: "molluscs", i18nKey: "alMolluscs", code: "MO" },
  { key: "mustard", i18nKey: "alMustard", code: "MU" },
  { key: "tree_nuts", i18nKey: "alTreeNuts", code: "TN" },
  { key: "peanuts", i18nKey: "alPeanuts", code: "PE" },
  { key: "sesame", i18nKey: "alSesame", code: "SE" },
  { key: "soya", i18nKey: "alSoya", code: "SY" },
  { key: "sulphites", i18nKey: "alSulphites", code: "SD" },
];

function _drawAllergenGrid(doc, y, unitName, matrix, pageW, pageH, M) {
  const A = FSA_ALLERGENS_PDF;
  const dishes = (matrix && matrix.dishes) || [];
  doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(13);
  doc.text(`${pt("allergenMatrix")}${unitName ? " — " + unitName : ""}`, M, y); y += 16;
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(9);
  const rev = matrix && matrix.last_review_date ? formatYmd(matrix.last_review_date) : "—";
  const by = (matrix && matrix.reviewed_by) || "—";
  doc.text(`${pt("lastReviewed")} ${rev}     |     ${pt("reviewedBy")} ${by}`, M, y); y += 8;
  doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 12;
  if (!dishes.length) {
    doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(10);
    doc.text(pt("noDishes"), M, y + 8); return y + 20;
  }
  const dishW = 150;
  const colW = (pageW - M * 2 - dishW) / A.length;
  const headH = 22;
  const drawHeader = () => {
    doc.setFillColor(15, 23, 42); doc.rect(M, y, pageW - M * 2, headH, "F");
    doc.setTextColor(255, 255, 255); setF(doc, "bold"); doc.setFontSize(9);
    doc.text(pt("dishMenuItem"), M + 5, y + 15);
    doc.setFontSize(8);
    A.forEach((al, i) => doc.text(al.code, M + dishW + i * colW + colW / 2, y + 15, { align: "center" }));
    y += headH;
  };
  drawHeader();
  setF(doc, "normal");
  dishes.forEach((d) => {
    doc.setFontSize(9);
    const nameLines = doc.splitTextToSize(String(d.name || "-"), dishW - 8);
    const rowH = Math.max(20, nameLines.length * 11 + 8);
    if (y + rowH > pageH - M - FOOTER) { doc.addPage(); y = M; drawHeader(); setF(doc, "normal"); }
    doc.setDrawColor(226, 232, 240); doc.rect(M, y, pageW - M * 2, rowH);
    doc.setTextColor(15, 23, 42); setF(doc, "normal"); doc.setFontSize(9);
    doc.text(nameLines, M + 5, y + 13);
    A.forEach((al, i) => {
      const x = M + dishW + i * colW;
      doc.setDrawColor(226, 232, 240); doc.line(x, y, x, y + rowH);
      if (d.allergens && d.allergens[al.key]) {
        doc.setFillColor(180, 83, 9); doc.rect(x + 1.5, y + 1.5, colW - 3, rowH - 3, "F");
        doc.setTextColor(255, 255, 255); setF(doc, "bold"); doc.setFontSize(9);
        doc.text("X", x + colW / 2, y + rowH / 2 + 3, { align: "center" });
        setF(doc, "normal");
      }
    });
    y += rowH;
  });
  y += 12;
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(8);
  doc.text(pt("key"), M, y + 8); y += 8;
  const legend = A.map((al) => `${al.code} = ${pt(al.i18nKey)}`).join("     ");
  doc.splitTextToSize(legend, pageW - M * 2).forEach((ln) => { if (y + 12 > pageH - M - FOOTER) { doc.addPage(); y = M; } doc.text(ln, M, y + 8); y += 11; });
  return y + 8;
}

function buildAllergenDoc(company, unitName, matrix) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  initFont(doc);
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 40;
  const co = company || _ctx.company || "";
  const ref = docRefId("AM");
  let y = M;
  doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(20);
  doc.text("SafeBook", M, y);
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(8);
  doc.text(`${pt("docRef")} ${ref}`, pageW - M, y - 8, { align: "right" }); y += 22;
  doc.setTextColor(51, 65, 85); setF(doc, "bold"); doc.setFontSize(15);
  doc.text(pt("allergenMatrixTitle"), M, y); y += 18;
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(10);
  doc.text(`${co ? co + " — " : ""}${formatDateTime(new Date())}`, M, y); y += 16;
  _drawAllergenGrid(doc, y, unitName, matrix, pageW, pageH, M);
  _footerAll(doc, ref, _ctx.terms, pageW, pageH, M);
  const fname = `allergen-matrix-${String(unitName || "unit").replace(/[^\w]+/g, "_")}.pdf`.toLowerCase();
  return { doc, fname, ref };
}

export function exportAllergenMatrix(company, unitName, matrix) {
  const { doc, fname } = buildAllergenDoc(company, unitName, matrix);
  doc.save(fname);
}

export function allergenMatrixFile(company, unitName, matrix) {
  const { doc, ref } = buildAllergenDoc(company, unitName, matrix);
  const blob = doc.output("blob");
  return { file: new File([blob], `${ref}.pdf`, { type: "application/pdf" }), ref };
}

// ============================================================================
// FSA SFBB Master Cleaning Schedule — tabular plan (per catering unit). Rendered
// standalone (trailer display) and appended to the catering EHO Audit Pack.
// ============================================================================
function _drawCleaningSchedule(doc, y, unitName, items, pageW, pageH, M) {
  const list = items || [];
  doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(13);
  doc.text(`${pt("cleaningSchedule")}${unitName ? " — " + unitName : ""}`, M, y); y += 16;
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(9);
  doc.text(pt("sfbbSubtitle"), M, y); y += 8;
  doc.setDrawColor(226, 232, 240); doc.line(M, y, pageW - M, y); y += 10;
  if (!list.length) { doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(10); doc.text(pt("noCleaningItems"), M, y + 8); return y + 20; }
  const cName = 108, cFreq = 66, cPre = 110, cMethod = pageW - M * 2 - cName - cFreq - cPre;
  const xs = [M, M + cName, M + cName + cFreq, M + cName + cFreq + cPre];
  const ws = [cName, cFreq, cPre, cMethod];
  const heads = [pt("hItemSurface"), pt("hFrequency"), pt("hPrecautions"), pt("hMethod")];
  const drawHead = () => {
    doc.setFillColor(15, 23, 42); doc.rect(M, y, pageW - M * 2, 20, "F");
    doc.setTextColor(255, 255, 255); setF(doc, "bold"); doc.setFontSize(8.5);
    heads.forEach((h, i) => doc.text(h, xs[i] + 4, y + 14));
    y += 20;
  };
  drawHead();
  setF(doc, "normal");
  list.forEach((it) => {
    doc.setFontSize(9);
    const cells = [
      doc.splitTextToSize(String(it.name || "-"), cName - 8),
      doc.splitTextToSize(cleanFreqLabel(it.frequency), cFreq - 8),
      doc.splitTextToSize(String(it.precautions || "-"), cPre - 8),
      doc.splitTextToSize(String(it.method || "-"), cMethod - 8),
    ];
    const maxLines = Math.max(...cells.map((c) => c.length));
    const rowH = maxLines * 11 + 8;
    if (y + rowH > pageH - M - FOOTER) { doc.addPage(); y = M; drawHead(); setF(doc, "normal"); }
    doc.setDrawColor(226, 232, 240); doc.rect(M, y, pageW - M * 2, rowH);
    ws.forEach((w, i) => { if (i > 0) doc.line(xs[i], y, xs[i], y + rowH); });
    doc.setTextColor(15, 23, 42); doc.setFontSize(9);
    cells.forEach((c, i) => { setF(doc, i === 0 ? "bold" : "normal"); doc.text(c, xs[i] + 4, y + 13); });
    setF(doc, "normal");
    y += rowH;
  });
  return y + 12;
}

function buildCleaningScheduleDoc(company, unitName, items) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  initFont(doc);
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 40;
  const co = company || _ctx.company || "";
  const ref = docRefId("CS");
  let y = M;
  doc.setTextColor(180, 83, 9); setF(doc, "bold"); doc.setFontSize(20); doc.text("SafeBook", M, y);
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(8); doc.text(`${pt("docRef")} ${ref}`, pageW - M, y - 8, { align: "right" }); y += 22;
  doc.setTextColor(51, 65, 85); setF(doc, "bold"); doc.setFontSize(15); doc.text(pt("cleaningScheduleTitle"), M, y); y += 18;
  doc.setTextColor(100, 116, 139); setF(doc, "normal"); doc.setFontSize(10); doc.text(`${co ? co + " — " : ""}${formatDateTime(new Date())}`, M, y); y += 16;
  _drawCleaningSchedule(doc, y, unitName, items, pageW, pageH, M);
  _footerAll(doc, ref, _ctx.terms, pageW, pageH, M);
  const fname = `cleaning-schedule-${String(unitName || "unit").replace(/[^\w]+/g, "_")}.pdf`.toLowerCase();
  return { doc, fname, ref };
}

export function exportCleaningSchedule(company, unitName, items) {
  const { doc, fname } = buildCleaningScheduleDoc(company, unitName, items);
  doc.save(fname);
}
